import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Stack,
  Alert,
  Tooltip,
} from '@mui/material';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';

const SYSTEM_TYPES = ['ХВС', 'ГВС', 'Отопление', 'Электросчетчик', 'УРТ'];
const COLLECTION_MODES = [
  { value: 'file_import', label: 'Импорт из файла' },
  { value: 'automatic', label: 'Автоматический сбор' },
];

const initialForm = () => ({
  manufacturer: '',
  system_type: '',
  serial_number: '',
  seal_date: '',
  verification_date: '',
  dev_eui: '',
  mqtt_topic: '',
  reading_collection_mode: 'automatic',
  comment: '',
  heat_seal_status: '',
  heat_operational_status: '',
});

const normalizeString = (value) => (value ?? '');
const normalizeNullable = (value) => (value && value.trim().length ? value : null);

const FieldLabel = ({ label, title }) => (
  <Stack component="span" direction="row" spacing={0.5} alignItems="center">
    <span>{label}</span>
    <Tooltip title={title} arrow>
      <HelpOutlineRoundedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
    </Tooltip>
  </Stack>
);

export default function EditDeviceDialog({ open, device, onClose, onSave }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const initialValues = useMemo(() => {
    if (!device) return initialForm();
    return {
      manufacturer: normalizeString(device.manufacturer),
      system_type: normalizeString(device.system_type),
      serial_number: normalizeString(device.serial_number),
      seal_date: normalizeString(device.seal_date),
      verification_date: normalizeString(device.verification_date),
      dev_eui: normalizeString(device.dev_eui).toUpperCase(),
      mqtt_topic: normalizeString(device.mqtt_topic),
      reading_collection_mode:
        device.reading_collection_mode === 'file_import' ? 'file_import' : 'automatic',
      comment: normalizeString(device.description ?? device.comment),
      heat_seal_status: normalizeString(device.heat_seal_status),
      heat_operational_status: normalizeString(device.heat_operational_status),
    };
  }, [device]);

  useEffect(() => {
    if (open) {
      setForm(initialValues);
      setError('');
    }
  }, [open, initialValues]);

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const pendingPatch = useMemo(() => {
    if (!device) return null;
    const patch = {};
    if (form.manufacturer !== initialValues.manufacturer) {
      patch.manufacturer = normalizeNullable(form.manufacturer);
    }
    if (form.system_type !== initialValues.system_type) {
      patch.system_type = normalizeNullable(form.system_type);
    }
    if (form.serial_number !== initialValues.serial_number) {
      patch.serial_number = normalizeNullable(form.serial_number);
    }
    if (form.seal_date !== initialValues.seal_date) {
      patch.seal_date = normalizeNullable(form.seal_date);
    }
    if (form.verification_date !== initialValues.verification_date) {
      patch.verification_date = normalizeNullable(form.verification_date);
    }
    const nextDevEui = form.dev_eui.toUpperCase().trim();
    if (nextDevEui !== initialValues.dev_eui) {
      patch.dev_eui = normalizeNullable(nextDevEui);
    }
    const nextTopic = form.mqtt_topic.trim();
    if (nextTopic !== initialValues.mqtt_topic) {
      patch.mqtt_topic = normalizeNullable(nextTopic);
    }
    if (form.reading_collection_mode !== initialValues.reading_collection_mode) {
      patch.reading_collection_mode = form.reading_collection_mode;
    }
    if (form.comment !== initialValues.comment) {
      const value = form.comment;
      const normalized = value.length ? value : null;
      patch.comment = normalized;
      patch.description = normalized;
    }
    if (form.heat_seal_status !== initialValues.heat_seal_status) {
      patch.heat_seal_status = normalizeNullable(form.heat_seal_status);
    }
    if (form.heat_operational_status !== initialValues.heat_operational_status) {
      patch.heat_operational_status = normalizeNullable(form.heat_operational_status);
    }
    return Object.keys(patch).length ? patch : null;
  }, [device, form, initialValues]);

  const handleSubmit = async (event) => {
    event?.preventDefault?.();
    if (!device || !onSave) {
      onClose?.();
      return;
    }
    if (!pendingPatch) {
      onClose?.();
      return;
    }
    try {
      setSaving(true);
      setError('');
      await onSave(device, pendingPatch);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Не удалось сохранить изменения. Попробуйте ещё раз.');
    } finally {
      setSaving(false);
    }
  };

  const disabled = saving || !pendingPatch;
  const isHeatAllocator = form.system_type === 'УРТ';
  const sealWasBroken = ['broken', 'opened', 'damaged'].includes(
    String(initialValues.heat_seal_status || '').toLowerCase()
  );
  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Редактировать устройство</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              select
              label="Тип счётчика"
              value={form.system_type}
              onChange={handleChange('system_type')}
              helperText="Выберите тип или оставьте пустым"
            >
              <MenuItem value="">—</MenuItem>
              {SYSTEM_TYPES.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Производитель"
              value={form.manufacturer}
              onChange={handleChange('manufacturer')}
            />

            <TextField
              label="Заводской номер"
              value={form.serial_number}
              onChange={handleChange('serial_number')}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Дата пломбировки"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.seal_date}
                onChange={handleChange('seal_date')}
                fullWidth
              />
              <TextField
                label="Дата поверки"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={form.verification_date}
                onChange={handleChange('verification_date')}
                fullWidth
              />
            </Stack>

            {isHeatAllocator ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  label="Состояние прибора"
                  value={form.heat_operational_status}
                  onChange={handleChange('heat_operational_status')}
                  helperText="Неработающий УРТ переводит квартиру на расчёт по площади"
                  fullWidth
                >
                  <MenuItem value="">Не указано</MenuItem>
                  <MenuItem value="active">Работает</MenuItem>
                  <MenuItem value="not_working">Не работает</MenuItem>
                  <MenuItem value="removed">Демонтирован</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Состояние пломбы"
                  value={form.heat_seal_status}
                  onChange={handleChange('heat_seal_status')}
                  helperText="При нарушенной пломбе квартира рассчитывается по площади"
                  disabled={sealWasBroken}
                  fullWidth
                >
                  <MenuItem value="">Не указано</MenuItem>
                  <MenuItem value="sealed" disabled={sealWasBroken}>На месте</MenuItem>
                  <MenuItem value="broken">Вскрыта или повреждена</MenuItem>
                </TextField>
              </Stack>
            ) : null}

            <TextField
              select
              label="Режим сбора"
              value={form.reading_collection_mode}
              onChange={handleChange('reading_collection_mode')}
            >
              {COLLECTION_MODES.map((mode) => (
                <MenuItem key={mode.value} value={mode.value}>{mode.label}</MenuItem>
              ))}
            </TextField>

            {form.reading_collection_mode === 'automatic' ? (
              <>
                <TextField
                  label={
                    <FieldLabel
                      label="DevEUI"
                      title="Идентификатор прибора в сети LoRaWAN."
                    />
                  }
                  value={form.dev_eui}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, dev_eui: event.target.value.toUpperCase() }))
                  }
                />

                <TextField
                  label={
                    <FieldLabel
                      label="MQTT топик"
                      title="Канал прямой передачи показаний по MQTT."
                    />
                  }
                  value={form.mqtt_topic}
                  onChange={handleChange('mqtt_topic')}
                />
              </>
            ) : null}

            <TextField
              label="Комментарий"
              value={form.comment}
              onChange={handleChange('comment')}
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>Отмена</Button>
          <Button type="submit" variant="contained" disabled={disabled}>
            Сохранить
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
